import { useRef, useMemo, ReactNode, ComponentProps } from "react";
import { LitMenu, MdMenu } from "../Menu/LitMenu";
import { sysColors } from "@/assets/theme/colors";
import { ReactWebComponent } from "@lit/react";
import classNames from "classnames";

type LitMenuProps = Pick<ComponentProps<ReactWebComponent<MdMenu, {}>>, "positioning" | "anchorCorner" | "menuCorner">;

export type MenuProps = LitMenuProps & {
  AnchorComponent: ReactNode;
  content: ReactNode;
  variant: "rich" | "plain";
  color: "light" | "dark";
  anchorClassName?: string;
  noVerticalFlip?: boolean;
  closeDelay?: number;
  compactHoverArea?: boolean;
  compactTopPadding?: boolean;
};

const sizeClasses = {
  plain: "px-2 w-max max-w-[400px]",
  rich: "px-4 pb-4 w-[312px]"
};

const bgColors = {
  light: "sys-brand-surface",
  dark: "sys-brand-on-surface"
};

const textColorClasses = {
  light: "text-sys-brand-on-surface",
  dark: "text-sys-brand-surface"
};

const randomNum = () => (Math.random() * 10000000).toFixed(0);

export default function MenuTooltip({
  content,
  AnchorComponent,
  variant,
  color,
  anchorCorner: anchorCornerProp,
  menuCorner: menuCornerProp,
  positioning = "absolute",
  anchorClassName,
  noVerticalFlip,
  closeDelay = 0,
  compactHoverArea = false,
  compactTopPadding = false
}: MenuProps) {
  const anchorCorner = anchorCornerProp ?? (variant === "plain" ? "start-start" : "end-end");
  const menuCorner = menuCornerProp ?? (variant === "plain" ? "end-start" : "start-end");
  const menuRef = useRef<MdMenu>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    menuRef.current?.show();
  };

  const hideTooltip = () => {
    if (closeDelay > 0) {
      closeTimeoutRef.current = setTimeout(() => {
        menuRef.current?.close();
      }, closeDelay);
    } else {
      menuRef.current?.close();
    }
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const anchorId = useMemo(() => `menu-tooltip-anchor-${randomNum()}`, []);

  const style = {
    "--md-menu-container-shape": variant === "plain" ? "4px" : "12px",
    "--md-menu-container-color": sysColors[bgColors[color]],
    minWidth: "60px"
  } as React.CSSProperties;

  const defaultHoverAreaClass = compactHoverArea ? "md:-mx-2 md:px-2" : "md:-mx-10 md:px-10";

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      data-testid="menu-tooltip"
    >
      {/* Adding the spacing to allow for larger hover area */}
      <div id={anchorId} role="button" className={`flex items-center ${anchorClassName || defaultHoverAreaClass} `}>
        {!!AnchorComponent && AnchorComponent}
      </div>
      <LitMenu
        quick
        anchor={anchorId}
        ref={menuRef}
        {...{ style, anchorCorner, menuCorner, positioning, noVerticalFlip }}
      >
        <div
          role="tooltip"
          className={classNames(
            sizeClasses[variant],
            variant === "rich" && (compactTopPadding ? "pt-2" : "pt-4"),
            textColorClasses[color],
            "whitespace-pre-line cursor-default"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={hideTooltip}
        >
          {content}
        </div>
      </LitMenu>
    </div>
  );
}
